import { apiConfig, http } from "@api/base";
import { getSearchTotal, prepareSumQuery } from "@api/utils";
import { sessionManager } from "@authentication/services/SessionManager";
import _isEmpty from "lodash/isEmpty";
import { generatePath } from "react-router-dom";
import { serializer } from "./serializers";

const apiPaths = {
  // Main configurable operations
  list: "/configurables",
  item: "/configurables/:configurableId",
  create: "/configurables",
  update: "/configurables/:configurableId",
  delete: "/configurables/:configurableId",

  // Value operations
  updateValue: "/configurables/:configurableId/value",

  // Name-based operations
  getByName: "/configurables/name/:configName",

  // Bulk operations
  bulkUpdate: "/configurables/bulk-update",

  // Convenience endpoints
  active: "/configurables/active",

  // Test endpoint
  test: "/configurables/test",
};

/**
 * Get a specific configurable by ID
 */
const get = async (configurableId) => {
  const path = generatePath(apiPaths.item, { configurableId });
  const response = await http.get(path);

  if (response.data) {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

/**
 * Get configurable by name
 */
const getByName = async (configName) => {
  const path = generatePath(apiPaths.getByName, { configName });
  const response = await http.get(path);

  if (response.data) {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

/**
 * Create a new configurable
 */
const create = async (configurableData) => {
  const currentUser = sessionManager.user;
  const backendPayload = serializer.toCreateJSON(configurableData);

  // Add user context
  backendPayload.created_by =
    currentUser?.username || currentUser?.email || "Frontend";

  const response = await http.post(apiPaths.create, backendPayload);

  if (response.data) {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

/**
 * Update an existing configurable
 */
const update = async (configurableId, configurableData) => {
  const currentUser = sessionManager.user;
  const path = generatePath(apiPaths.update, { configurableId });
  const backendPayload = serializer.toUpdateJSON(configurableData);

  backendPayload.updated_by = currentUser?.username || currentUser?.email;

  const response = await http.put(path, backendPayload);

  if (response.data) {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

/**
 * Update only the configurable value
 */
const updateValue = async (configurableId, value) => {
  const path = generatePath(apiPaths.updateValue, { configurableId });
  const backendPayload = serializer.toValueUpdateJSON(value);

  const response = await http.put(path, backendPayload);

  if (response.data) {
    response.data = serializer.fromValueJSON(response.data);
  }

  return response;
};

/**
 * Delete a configurable (soft delete)
 */
const deleteConfigurable = async (configurableId) => {
  const path = generatePath(apiPaths.delete, { configurableId });
  const response = await http.delete(path);
  return response;
};

/**
 * Bulk update multiple configurables
 */
const bulkUpdate = async (updates) => {
  const backendPayload = serializer.toBulkUpdateJSON(updates);
  const response = await http.put(apiPaths.bulkUpdate, backendPayload);

  if (response.data) {
    response.data = serializer.fromBulkJSON(response.data);
  }

  return response;
};

/**
 * Get all active configurables (minimal data)
 */
const getActive = async () => {
  const response = await http.get(apiPaths.active);

  if (response.data) {
    const searchResult = serializer.fromSearchJSON(response);
    response.data.hits = searchResult.hits;
    response.data.total = searchResult.total;
    response.data.pagination = searchResult.pagination;
  } else {
    response.data = { hits: [], total: 0 };
  }

  return response;
};

/**
 * Query Builder for configurables
 */
class ConfigurableQueryBuilder {
  constructor() {
    this.nameQuery = [];
    this.searchQuery = "";
    this.isActiveQuery = true;
    this.size = "";
    this.page = "";
  }

  withName(name) {
    if (!name || _isEmpty(name)) {
      throw new TypeError("Name argument missing");
    }
    this.nameQuery.push(`name:"${prepareSumQuery(name)}"`);
    return this;
  }

  withSearch(searchTerm) {
    if (!searchTerm || _isEmpty(searchTerm)) {
      throw new TypeError("Search term argument missing");
    }
    this.searchQuery = searchTerm;
    return this;
  }

  activeOnly() {
    this.isActiveQuery = true;
    return this;
  }

  includeInactive() {
    this.isActiveQuery = false;
    return this;
  }

  withSize(size) {
    if (size > 0) this.size = `per_page=${size}`;
    return this;
  }

  withPage(page = 1) {
    if (page > 0) this.page = `page=${page}`;
    return this;
  }

  qs() {
    const queryParams = [];

    // Add name filter
    if (this.nameQuery.length > 0) {
      queryParams.push(
        `name=${encodeURIComponent(this.nameQuery.join(" OR "))}`
      );
    }

    // Add search term
    if (this.searchQuery) {
      queryParams.push(`search=${encodeURIComponent(this.searchQuery)}`);
    }

    // Add active status
    queryParams.push(`is_active=${this.isActiveQuery}`);

    // Add pagination
    if (this.size) queryParams.push(this.size);
    if (this.page) queryParams.push(this.page);

    return queryParams.join("&");
  }
}

const queryBuilder = () => {
  return new ConfigurableQueryBuilder();
};

/**
 * List configurables with optional filtering
 */
const list = async (query = "") => {
  const queryString = query ? `?${query}` : "";
  const response = await http.get(`${apiPaths.list}${queryString}`);

  if (response.data) {
    const searchResult = serializer.fromSearchJSON(response);
    response.data.hits = searchResult.hits;
    response.data.total = searchResult.total;
    response.data.pagination = searchResult.pagination || {};
  } else {
    response.data = { hits: [], total: 0 };
  }

  return response;
};

/**
 * Count configurables matching query
 */
const count = async (query) => {
  const response = await http.get(`${apiPaths.list}?${query}`);
  response.data =
    response.data.pagination?.total || getSearchTotal(response.data.hits) || 0;
  return response;
};

/**
 * Get configurables by name pattern
 */
const getByNamePattern = async (namePattern, limit = 50) => {
  const query = queryBuilder().withName(namePattern).withSize(limit).qs();
  return await list(query);
};

/**
 * Search configurables by term
 */
const search = async (searchTerm, limit = 50) => {
  const query = queryBuilder().withSearch(searchTerm).withSize(limit).qs();
  return await list(query);
};

/**
 * Get all configurables (active and inactive)
 */
const getAll = async (limit = 100) => {
  const query = queryBuilder().includeInactive().withSize(limit).qs();
  return await list(query);
};

/**
 * Get configuration value by name (convenience method)
 */
const getValue = async (configName) => {
  try {
    const response = await getByName(configName);
    if (response.data && response.data.value !== undefined) {
      return {
        status: "success",
        data: response.data.value,
        configurable: response.data,
      };
    }
    return {
      status: "error",
      message: `Configuration '${configName}' not found`,
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to get configuration value",
      error: error.message,
    };
  }
};

/**
 * Set configuration value by name (convenience method)
 */
const setValue = async (configName, value) => {
  try {
    // First get the configurable to find its ID
    const getResponse = await getByName(configName);
    if (getResponse.data && getResponse.data.id) {
      return await updateValue(getResponse.data.id, value);
    }
    return {
      status: "error",
      message: `Configuration '${configName}' not found`,
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to set configuration value",
      error: error.message,
    };
  }
};

/**
 * Get multiple configuration values by names
 */
const getMultipleValues = async (configNames) => {
  try {
    const promises = configNames.map((name) => getValue(name));
    const responses = await Promise.allSettled(promises);

    const result = {};
    configNames.forEach((name, index) => {
      const response = responses[index];
      if (
        response.status === "fulfilled" &&
        response.value.status === "success"
      ) {
        result[name] = response.value.data;
      } else {
        result[name] = null;
      }
    });

    return {
      status: "success",
      data: result,
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to get multiple configuration values",
      error: error.message,
    };
  }
};

/**
 * Bulk update configurations by name
 */
const bulkUpdateByName = async (nameValuePairs) => {
  try {
    // Convert name-value pairs to ID-value pairs
    const updates = [];

    for (const [name, value] of Object.entries(nameValuePairs)) {
      try {
        const getResponse = await getByName(name);
        if (getResponse.data && getResponse.data.id) {
          updates.push({
            id: getResponse.data.id,
            name: name,
            value: value,
          });
        }
      } catch (error) {
        // Skip failed configurations silently
      }
    }

    if (updates.length === 0) {
      return {
        status: "error",
        message: "No valid configurations found for update",
      };
    }

    return await bulkUpdate(updates);
  } catch (error) {
    return {
      status: "error",
      message: "Failed to bulk update configurations",
      error: error.message,
    };
  }
};

/**
 * Create quick configuration with default settings
 */
const createQuick = async (name, value, description = "") => {
  const configurableData = serializer.createQuickConfigData(
    name,
    value,
    description
  );
  return await create(configurableData);
};

/**
 * Export configurations to JSON
 */
const exportToJSON = async (filters = {}) => {
  try {
    const builder = queryBuilder().withSize(1000);

    // Apply filters
    if (filters.namePattern) builder.withName(filters.namePattern);
    if (filters.search) builder.withSearch(filters.search);
    if (filters.includeInactive) builder.includeInactive();

    const query = builder.qs();
    const response = await list(query);

    if (response.data.hits) {
      const exportData = serializer.exportToJSON(response.data.hits);
      return {
        status: "success",
        data: exportData,
        filename: `configurables_export_${
          new Date().toISOString().split("T")[0]
        }.json`,
      };
    }

    return {
      status: "error",
      message: "No data available for export",
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to export data",
      error: error.message,
    };
  }
};

/**
 * Test API connectivity
 */
const testConnection = async () => {
  const response = await http.get(apiPaths.test);
  return response;
};

/**
 * Get configuration statistics
 */
const getStats = async () => {
  try {
    const [activeResponse, allResponse] = await Promise.allSettled([
      getActive(),
      getAll(),
    ]);

    const activeCount =
      activeResponse.status === "fulfilled"
        ? activeResponse.value.data.total || 0
        : 0;
    const totalCount =
      allResponse.status === "fulfilled"
        ? allResponse.value.data.total || 0
        : 0;
    const inactiveCount = totalCount - activeCount;

    return {
      status: "success",
      data: {
        total: totalCount,
        active: activeCount,
        inactive: inactiveCount,
        activePercentage:
          totalCount > 0 ? Math.round((activeCount / totalCount) * 100) : 0,
      },
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to get configuration statistics",
      error: error.message,
    };
  }
};

/**
 * Validate configuration value format
 */
const validateValue = async (configurableId, value) => {
  try {
    // Get the configuration for validation
    const configResponse = await get(configurableId);

    if (configResponse.data) {
      const validationResult = serializer.validateConfigValue(
        configResponse.data,
        value
      );
      return {
        status: "success",
        data: validationResult,
      };
    }

    return {
      status: "error",
      message: "Configuration not found for validation",
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to validate configuration value",
      error: error.message,
    };
  }
};

export const configurableApi = {
  searchBaseURL: `${apiConfig.baseURL}${apiPaths.list}`,
  query: queryBuilder,

  // Core operations
  list,
  get,
  create,
  update,
  delete: deleteConfigurable,
  count,

  // Value operations
  updateValue,
  getValue,
  setValue,
  getMultipleValues,
  validateValue,

  // Name-based operations
  getByName,
  getByNamePattern,

  // Bulk operations
  bulkUpdate,
  bulkUpdateByName,

  // Convenience methods
  getActive,
  getAll,
  search,
  createQuick,

  // Data operations
  exportToJSON,
  getStats,

  // Utility
  testConnection,

  // Serializer
  serializer,
};
